import { useEffect, useMemo, useRef, useState } from "react";
import { IconArrowRight, IconSearch } from "../../icons";

export interface PaletteItem {
  id: string;
  group: string;
  label: string;
  /** Small mono text on the right: where it goes, or the shortcut. */
  hint?: string;
  icon: React.ReactNode;
  run: () => void;
}

/** Cmd/Ctrl+K: jump to any demo, tab or page, or run a small action, from the keyboard. */
export default function CommandPalette({ open, onClose, items }: { open: boolean; onClose: () => void; items: PaletteItem[] }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => { if (open) { setQ(""); setActive(0); setTimeout(() => input.current?.focus(), 0); } }, [open]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? items.filter((i) => `${i.label} ${i.hint ?? ""} ${i.group}`.toLowerCase().includes(needle)) : items;
  }, [items, q]);
  useEffect(() => setActive(0), [q]);
  useEffect(() => { list.current?.querySelector<HTMLElement>(".on")?.scrollIntoView({ block: "nearest" }); }, [active]);

  if (!open) return null;

  const run = (i: PaletteItem | undefined) => { if (!i) return; onClose(); i.run(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(shown.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); run(shown[active]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  let lastGroup = "";
  return (
    <div className="palette-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Command menu" onKeyDown={onKey}>
        <div className="palette-input">
          <IconSearch />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to a demo, tab or page…" aria-label="Search" role="combobox" aria-expanded="true" aria-controls="palette-list" />
          <kbd>Esc</kbd>
        </div>
        <div className="palette-list" id="palette-list" role="listbox" ref={list}>
          {shown.length === 0 && <p className="small muted palette-empty">Nothing matches “{q}”.</p>}
          {shown.map((i, n) => {
            const head = i.group !== lastGroup ? <div className="eyebrow palette-group" key={`g-${i.group}`}>{i.group}</div> : null;
            lastGroup = i.group;
            return (
              <div key={i.id}>
                {head}
                <button type="button" role="option" aria-selected={n === active} className={`palette-item ${n === active ? "on" : ""}`} onMouseMove={() => setActive(n)} onClick={() => run(i)}>
                  <span className="pi-icon">{i.icon}</span>
                  <span className="pi-label">{i.label}</span>
                  {i.hint && <span className="pi-hint">{i.hint}</span>}
                </button>
              </div>
            );
          })}
        </div>
        <div className="palette-foot small">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>↵</kbd> open <IconArrowRight className="icon icon-xs" /></span>
        </div>
      </div>
    </div>
  );
}
